import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Loader, Crown, Calendar, Gift } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('checking');  // checking, success, error
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    }
  }, [sessionId]);

  const checkPaymentStatus = async (attempt = 0) => {
    const maxAttempts = 5;
    
    if (attempt >= maxAttempts) {
      setStatus('error');
      setError('Payment verification timed out. Please check your email for confirmation.');
      return;
    }

    try {
      const response = await axios.get(`${API}/subscriptions/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setSubscriptionData(response.data);
      } else {
        // Keep polling
        setTimeout(() => checkPaymentStatus(attempt + 1), 2000);
      }
    } catch (err) {
      console.error('Payment check error:', err);
      setTimeout(() => checkPaymentStatus(attempt + 1), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-2xl w-full">
          {status === 'checking' && (
            <div className="bg-gray-900 border border-yellow-500/20 rounded-2xl p-12 text-center">
              <Loader className="w-16 h-16 text-yellow-400 mx-auto mb-6 animate-spin" />
              <h1 className="text-3xl font-bold text-white mb-4">
                Verifying Your Payment...
              </h1>
              <p className="text-gray-400">
                Please wait while we confirm your subscription payment.
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="bg-gradient-to-br from-yellow-400/10 to-transparent border-2 border-yellow-400 rounded-2xl p-12">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-12 h-12 text-green-400" />
                </div>
                <h1 className="text-4xl font-bold text-white mb-4">
                  Welcome to Elite Membership! 🎉
                </h1>
                <p className="text-xl text-gray-300">
                  Your payment was successful and your membership is now active!
                </p>
              </div>

              <div className="bg-black/30 rounded-xl p-8 mb-8">
                <h2 className="text-2xl font-bold text-yellow-400 mb-6 flex items-center justify-center">
                  <Crown className="w-8 h-8 mr-3" />
                  Your Membership Benefits
                </h2>
                
                <div className="grid gap-4">
                  <div className="flex items-start space-x-4">
                    <Calendar className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">3 Premium IV Treatments</p>
                      <p className="text-gray-400 text-sm">One treatment per month for the next 3 months</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <Gift className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">1 FREE IM Injection</p>
                      <p className="text-gray-400 text-sm">Choose from our full menu of add-ons</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <CheckCircle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">VIP Member Status</p>
                      <p className="text-gray-400 text-sm">Priority booking and exclusive benefits</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-400/10 border border-yellow-400/30 rounded-xl p-6 mb-8">
                <h3 className="text-lg font-bold text-yellow-400 mb-3">📧 What's Next?</h3>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start">
                    <span className="text-yellow-400 mr-2">•</span>
                    <span>Check your email for your membership confirmation</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-400 mr-2">•</span>
                    <span>Book your first treatment at your convenience</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-400 mr-2">•</span>
                    <span>Mention your membership when booking to apply your benefits</span>
                  </li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => navigate('/services')}
                  className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-4 px-6 rounded-lg transition-all"
                >
                  Book Your First Treatment
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="flex-1 bg-gray-800 hover:bg-gray-700 text-white font-bold py-4 px-6 rounded-lg transition-all"
                >
                  Return Home
                </button>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="bg-gray-900 border border-red-500/30 rounded-2xl p-12 text-center">
              <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">⚠️</span>
              </div>
              <h1 className="text-3xl font-bold text-white mb-4">
                Payment Verification Issue
              </h1>
              <p className="text-gray-400 mb-8">
                {error}
              </p>
              <button
                onClick={() => navigate('/membership')}
                className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-3 px-8 rounded-lg transition-all"
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SubscriptionSuccessPage;
